import { Calendar, Check, Edit2, MapPin, Palette, Plus, RefreshCw, Save, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { themes } from '../config/themes';
import { useFiscalYears } from '../contexts/FiscalYearContext';
import { useTheme } from '../contexts/ThemeContext';
import { computeRate } from '../lib/rateCalculator';
import { savedAddressService } from '../services/savedAddressService';
import { savedPurposeService } from '../services/savedPurposeService';
import { settingsService } from '../services/settingsService';
import { tripService } from '../services/tripService';
import type {
    CreateSavedAddressDTO,
    FiscalMonth,
    FiscalYear,
    SavedAddress,
    SavedPurpose,
    UpsertSettingsDTO,
} from '../types';
import { TripType } from '../types';
import AddressAutocomplete from './AddressAutocomplete';

type Tab = 'general' | 'appearance' | 'years';

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const { fiscalYears, refetch: refetchFiscalYears } = useFiscalYears();
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // General settings
  const [settings, setSettings] = useState<UpsertSettingsDTO>({
    name: '',
    homeAddress: '',
    licensePlate: '',
    cvr: '',
  });

  // Fiscal years & months
  const [selectedYear, setSelectedYear] = useState<FiscalYear | null>(null);
  const [fiscalMonths, setFiscalMonths] = useState<FiscalMonth[]>([]);
  const [showYearForm, setShowYearForm] = useState(false);
  const [newYear, setNewYear] = useState({
    year: new Date().getFullYear(),
    rateUnder20000: 3.56,
    rateOver20000: 2.0,
    endDay: 31,
  });

  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [addingAddress, setAddingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState<CreateSavedAddressDTO>({
    label: '',
    address: '',
    defaultPurpose: null,
  });

  // Saved purposes
  const [savedPurposes, setSavedPurposes] = useState<SavedPurpose[]>([]);
  const [addingPurpose, setAddingPurpose] = useState(false);
  const [savingPurpose, setSavingPurpose] = useState(false);
  const [newPurposeLabel, setNewPurposeLabel] = useState('');

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  /**
   * Analyse all business trips using threshold rate logic and show the
   * correct total vs the flat rate total as a toast.
   * Note: Azure does not yet persist ratePerKm/totalAmount per trip — this
   * is a read-only analysis. A backend schema migration is required to store
   * per-trip amounts.
   */
  const recalculateAllRates = async () => {
    setRecalculating(true);
    try {
      const tripsPage = await tripService.getTrips({}, 1, 10000);
      const businessTrips = tripsPage.data
        .filter((t) => t.type === TripType.Business)
        .sort((a, b) => a.date.localeCompare(b.date));

      if (businessTrips.length === 0) {
        showToast('Ingen erhvervsture fundet', 'error');
        return;
      }

      const kmByYear: Record<number, number> = {};
      let thresholdTotal = 0;
      let flatTotal = 0;

      for (const trip of businessTrips) {
        const year = new Date(trip.date).getFullYear();
        const fy = fiscalYears.find((y) => y.year === year);
        const rateUnder = fy?.rateUnder20000 ?? 3.56;
        const rateOver = fy?.rateOver20000 ?? 2.0;
        const kmSoFar = kmByYear[year] ?? 0;
        const rateResult = computeRate(kmSoFar, Number(trip.distance), rateUnder, rateOver);
        kmByYear[year] = kmSoFar + Number(trip.distance);
        thresholdTotal += rateResult.totalAmount;
        flatTotal += Number(trip.distance) * rateUnder;
      }

      const diff = parseFloat((thresholdTotal - flatTotal).toFixed(2));
      const diffStr =
        diff === 0
          ? 'ingen forskel'
          : `${diff > 0 ? '+' : ''}${diff.toFixed(2)} kr ift. fast sats`;

      showToast(
        `${businessTrips.length} ture analyseret — korrekt: ${thresholdTotal.toFixed(2)} kr (${diffStr})`,
        'success',
      );
    } catch {
      showToast('Fejl ved analyse af takster', 'error');
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [s, addresses, purposes] = await Promise.all([
          settingsService.getSettings(),
          savedAddressService.getSavedAddresses(),
          savedPurposeService.getSavedPurposes(),
        ]);
        if (s) {
          setSettings({
            name: s.name,
            homeAddress: s.homeAddress,
            licensePlate: s.licensePlate,
            cvr: s.cvr,
          });
        }
        setSavedAddresses(addresses);
        setSavedPurposes(purposes);
      } catch {
        showToast('Kunne ikke indlæse indstillinger', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      settingsService
        .getFiscalMonths(selectedYear.id)
        .then(setFiscalMonths)
        .catch(() => showToast('Kunne ikke indlæse måneder', 'error'));
    }
  }, [selectedYear]);

  // ── General settings ────────────────────────────────────────────────────────

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsService.upsertSettings(settings);
      showToast('Indstillinger gemt!', 'success');
    } catch {
      showToast('Fejl ved gemning af indstillinger', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Fiscal years ─────────────────────────────────────────────────────────────

  const handleCreateYear = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsService.createFiscalYear(newYear);
      showToast('År oprettet med 12 måneder!', 'success');
      setShowYearForm(false);
      setNewYear({ year: new Date().getFullYear() + 1, rateUnder20000: 3.56, rateOver20000: 2.0, endDay: 31 });
      await refetchFiscalYears();
    } catch {
      showToast('Fejl ved oprettelse af år', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateYear = async (
    yearId: string,
    updates: Pick<FiscalYear, 'rateUnder20000' | 'rateOver20000'>
  ) => {
    try {
      await settingsService.updateFiscalYear(yearId, updates);
      await refetchFiscalYears();
      if (selectedYear?.id === yearId) {
        const updated = fiscalYears.find(y => y.id === yearId);
        if (updated) setSelectedYear(updated);
      }
    } catch {
      showToast('Fejl ved opdatering af år', 'error');
    }
  };

  const handleDeleteYear = async (yearId: string) => {
    if (!confirm('Er du sikker på at du vil slette dette år og alle tilhørende måneder?')) return;
    try {
      await settingsService.deleteFiscalYear(yearId);
      showToast('År slettet!', 'success');
      await refetchFiscalYears();
      if (selectedYear?.id === yearId) {
        setSelectedYear(null);
        setFiscalMonths([]);
      }
    } catch {
      showToast('Fejl ved sletning af år', 'error');
    }
  };

  // ── Fiscal months ──────────────────────────────────────────────────────────

  const handleUpdateMonth = async (
    monthId: string,
    updates: Pick<FiscalMonth, 'startDate' | 'endDate'>
  ) => {
    try {
      const result = await settingsService.updateFiscalMonth(monthId, updates);
      setFiscalMonths(fiscalMonths.map((m) => (m.id === monthId ? result : m)));
    } catch {
      showToast('Fejl ved opdatering af måned', 'error');
    }
  };

  // ── Saved addresses ──────────────────────────────────────────────────────────

  const handleAddAddress = async () => {
    if (!newAddress.label.trim() || !newAddress.address.trim()) return;
    setSavingAddress(true);
    try {
      const created = await savedAddressService.createSavedAddress({
        label: newAddress.label.trim(),
        address: newAddress.address.trim(),
        defaultPurpose: newAddress.defaultPurpose?.trim() || null,
      });
      setSavedAddresses((prev) => [...prev, created]);
      setNewAddress({ label: '', address: '', defaultPurpose: null });
      setAddingAddress(false);
    } catch {
      showToast('Fejl ved oprettelse af adresse', 'error');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    if (!confirm('Slet denne adresse?')) return;
    try {
      await savedAddressService.deleteSavedAddress(id);
      setSavedAddresses((prev) => prev.filter((a) => a.id !== id));
    } catch {
      showToast('Fejl ved sletning af adresse', 'error');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await savedAddressService.setDefaultSavedAddress(id);
      setSavedAddresses((prev) =>
        prev.map((a) => ({ ...a, isDefault: a.id === id }))
      );
    } catch {
      showToast('Fejl ved ændring af standardadresse', 'error');
    }
  };

  const handleUpdateAddressPurpose = async (id: string, purpose: string) => {
    try {
      const updated = await savedAddressService.updateAddressPurpose(id, purpose || null);
      setSavedAddresses((prev) => prev.map((a) => (a.id === id ? updated : a)));
    } catch {
      showToast('Fejl ved opdatering af formål', 'error');
    }
  };

  // ── Saved purposes ───────────────────────────────────────────────────────────

  const handleAddPurpose = async () => {
    if (!newPurposeLabel.trim()) return;
    setSavingPurpose(true);
    try {
      const created = await savedPurposeService.createSavedPurpose({ label: newPurposeLabel.trim() });
      setSavedPurposes((prev) => [...prev, created]);
      setNewPurposeLabel('');
      setAddingPurpose(false);
    } catch {
      showToast('Fejl ved oprettelse af formål', 'error');
    } finally {
      setSavingPurpose(false);
    }
  };

  const handleDeletePurpose = async (id: string) => {
    if (!confirm('Slet dette formål?')) return;
    try {
      await savedPurposeService.deleteSavedPurpose(id);
      setSavedPurposes((prev) => prev.filter((p) => p.id !== id));
    } catch {
      showToast('Fejl ved sletning af formål', 'error');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p className="text-gray-600 mt-4">Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg font-semibold text-sm transition-all ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          {(['general', 'appearance', 'years'] as Tab[]).map((tab) => {
            const labels: Record<Tab, string> = {
              general: 'Generelt',
              appearance: 'Udseende',
              years: 'År & Måneder',
            };
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-semibold text-sm whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {labels[tab]}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── General tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div className="max-w-2xl">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
            Generelle indstillinger
          </h3>
          <form onSubmit={handleSaveSettings} className="space-y-5 sm:space-y-6">
            {[
              {
                id: 'name',
                label: 'Navn',
                placeholder: 'F.eks. Anders Andersen',
                hint: 'Dit fulde navn som skal vises på rapporter',
                key: 'name' as const,
              },
              {
                id: 'licensePlate',
                label: 'Nummerplade',
                placeholder: 'F.eks. AB12345',
                hint: 'Bilens registreringsnummer',
                key: 'licensePlate' as const,
              },
              {
                id: 'cvr',
                label: 'Evt. CVR',
                placeholder: 'F.eks. 12345678',
                hint: 'Dit CVR nummer hvis relevant',
                key: 'cvr' as const,
              },
              {
                id: 'homeAddress',
                label: 'Hjemmeadresse',
                placeholder: 'F.eks. Hovedgaden 1, 1000 København',
                hint: 'Din standard startadresse for beregninger',
                key: 'homeAddress' as const,
              },
            ].map(({ id, label, placeholder, hint, key }) => (
              <div key={id}>
                <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
                  {label}
                </label>
                <input
                  id={id}
                  type="text"
                  value={settings[key]}
                  onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={placeholder}
                />
                <p className="text-xs sm:text-sm text-gray-500 mt-1.5">{hint}</p>
              </div>
            ))}

            {/* ── Saved Addresses ─────────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Ekstra standard adresser
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Arbejdsplads, depot eller andre faste adresser — vises som genveje under{' '}
                    <strong>Opret kørsel</strong> ud over hjemmeadressen.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddingAddress((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Tilføj adresse
                </button>
              </div>

              {addingAddress && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Navn (f.eks. "Arbejdsplads")
                    </label>
                    <input
                      type="text"
                      value={newAddress.label}
                      onChange={(e) => setNewAddress((p) => ({ ...p, label: e.target.value }))}
                      placeholder="Arbejdsplads, Depot, Klient A..."
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Adresse
                    </label>
                    <AddressAutocomplete
                      value={newAddress.address}
                      onChange={(v) => setNewAddress((p) => ({ ...p, address: v }))}
                      placeholder="Søg adresse..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Tilknyt formål{' '}
                      <span className="font-normal text-gray-400">(valgfrit)</span>
                    </label>
                    <select
                      value={newAddress.defaultPurpose ?? ''}
                      onChange={(e) =>
                        setNewAddress((p) => ({
                          ...p,
                          defaultPurpose: e.target.value || null,
                        }))
                      }
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">— Intet formål —</option>
                      {savedPurposes.map((p) => (
                        <option key={p.id} value={p.label}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {savedPurposes.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1 italic">
                        Opret formål under "Egne formål" herunder for at kunne tilknytte dem.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setAddingAddress(false);
                        setNewAddress({ label: '', address: '', defaultPurpose: null });
                      }}
                      className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
                    >
                      Annuller
                    </button>
                    <button
                      type="button"
                      onClick={handleAddAddress}
                      disabled={savingAddress || !newAddress.label.trim() || !newAddress.address.trim()}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingAddress ? 'Gemmer...' : 'Gem adresse'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {savedAddresses.length === 0 && !addingAddress && (
                  <p className="text-sm text-gray-400 italic py-2">Ingen adresser gemt endnu.</p>
                )}
                {savedAddresses.map((addr) => (
                  <div
                    key={addr.id}
                    className={`p-3 rounded-xl border ${
                      addr.isDefault ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <MapPin
                        className={`w-4 h-4 flex-shrink-0 ${addr.isDefault ? 'text-blue-600' : 'text-gray-400'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {addr.label}
                          </p>
                          {addr.isDefault && (
                            <span className="flex-shrink-0 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-medium">
                              Standard
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{addr.address}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!addr.isDefault && (
                          <button
                            type="button"
                            onClick={() => handleSetDefault(addr.id)}
                            title="Sæt som standard"
                            className="p-1.5 text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 rounded-lg transition-colors"
                          >
                            <Star className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {addr.isDefault && (
                          <span className="p-1.5 text-yellow-500">
                            <Check className="w-3.5 h-3.5" />
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteAddress(addr.id)}
                          title="Slet adresse"
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {savedPurposes.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 pl-7">
                        <span className="text-xs text-gray-400 flex-shrink-0">Formål:</span>
                        <select
                          value={addr.defaultPurpose ?? ''}
                          onChange={(e) => handleUpdateAddressPurpose(addr.id, e.target.value)}
                          className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">— Intet formål —</option>
                          {savedPurposes.map((p) => (
                            <option key={p.id} value={p.label}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Saved Purposes ───────────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700">Egne formål</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Vises som hurtigvalg under <strong>Opret kørsel</strong> → Formål.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddingPurpose((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Tilføj formål
                </button>
              </div>

              {addingPurpose && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Formålsnavn
                    </label>
                    <input
                      type="text"
                      value={newPurposeLabel}
                      onChange={(e) => setNewPurposeLabel(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddPurpose()}
                      placeholder="F.eks. Kundemøde, Kursus, Lager..."
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setAddingPurpose(false);
                        setNewPurposeLabel('');
                      }}
                      className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
                    >
                      Annuller
                    </button>
                    <button
                      type="button"
                      onClick={handleAddPurpose}
                      disabled={savingPurpose || !newPurposeLabel.trim()}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingPurpose ? 'Gemmer...' : 'Gem formål'}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {savedPurposes.length === 0 && !addingPurpose && (
                  <p className="text-sm text-gray-400 italic py-1">
                    Ingen formål oprettet endnu. Klik "Tilføj formål" for at komme i gang.
                  </p>
                )}
                {savedPurposes.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700"
                  >
                    <span>{p.label}</span>
                    <button
                      type="button"
                      onClick={() => handleDeletePurpose(p.id)}
                      title="Slet"
                      className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
              <h4 className="font-semibold text-blue-900 mb-2">Om kilometertakster</h4>
              <p className="text-xs sm:text-sm text-blue-800">
                Kilometertakster administreres under "År & Måneder" fanen. Du kan oprette år med
                forskellige satser for km kørt under og over 20.000 km.
              </p>
              <button
                type="button"
                onClick={recalculateAllRates}
                disabled={recalculating}
                className="mt-3 flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
                <span>{recalculating ? 'Analyserer...' : 'Analysér takster'}</span>
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={saving}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold active:scale-95"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Gemmer...' : 'Gem indstillinger'}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Appearance tab ────────────────────────────────────────────────────── */}
      {activeTab === 'appearance' && (
        <div className="max-w-2xl">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
            Udseende & Tema
          </h3>
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Palette className="w-5 h-5 text-blue-600" />
                <h4 className="text-base font-bold text-gray-900">Vælg tema</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Skift mellem Classic og Smart design for at personalisere din oplevelse
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(['classic', 'smart'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`relative p-5 rounded-xl border-2 transition-all text-left ${
                      theme === t
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className="font-bold text-gray-900 mb-1">{themes[t].name}</h5>
                        <p className="text-xs text-gray-600">{themes[t].description}</p>
                      </div>
                      {theme === t && (
                        <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div
                        className={`w-8 h-8 rounded-${t === 'smart' ? 'xl' : 'lg'} ${t === 'smart' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'bg-blue-600 shadow-sm'}`}
                      />
                      <div
                        className={`w-8 h-8 rounded-${t === 'smart' ? 'xl' : 'lg'} border ${t === 'smart' ? 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-100' : 'bg-gray-100 border-gray-300'}`}
                      />
                      <div
                        className={`flex-1 h-3 ${t === 'smart' ? 'bg-gradient-to-r from-gray-200 to-gray-300 rounded-full' : 'bg-gray-200 rounded'}`}
                      />
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-900 font-medium mb-1">
                  Nuværende tema: {themes[theme].name}
                </p>
                <p className="text-xs text-blue-700">{themes[theme].description}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h5 className="font-semibold text-amber-900 mb-2">Om temaer</h5>
              <ul className="space-y-1 text-sm text-amber-800">
                <li>
                  • <strong>Classic:</strong> Traditionelt design med standard farver og enkle
                  former
                </li>
                <li>
                  • <strong>Smart:</strong> Moderne design med gradienter, bedre skygger og
                  optimeret til mobil
                </li>
                <li>• Dit valg gemmes automatisk i browseren</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ── Years & Months tab ────────────────────────────────────────────────── */}
      {activeTab === 'years' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">År & Måneder</h3>
            <button
              onClick={() => setShowYearForm(!showYearForm)}
              className="flex items-center justify-center space-x-2 px-5 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold active:scale-95"
            >
              <Plus className="w-5 h-5" />
              <span>Nyt år</span>
            </button>
          </div>

          {showYearForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Opret nyt år</h4>
              <form onSubmit={handleCreateYear} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">År</label>
                    <input
                      type="number"
                      value={newYear.year}
                      onChange={(e) => setNewYear({ ...newYear, year: parseInt(e.target.value) })}
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Slutdato i måneden
                    </label>
                    <select
                      value={newYear.endDay}
                      onChange={(e) => setNewYear({ ...newYear, endDay: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={31}>Sidste dag i måneden (Standard)</option>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day}>
                          Den {day}. i måneden
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      F.eks. vælg "Den 20." for perioder der kører 21. til 20. (januar starter dog
                      1., december slutter 31.)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sats under 20.000 km (kr)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newYear.rateUnder20000}
                      onChange={(e) =>
                        setNewYear({ ...newYear, rateUnder20000: parseFloat(e.target.value) })
                      }
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sats over 20.000 km (kr)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newYear.rateOver20000}
                      onChange={(e) =>
                        setNewYear({ ...newYear, rateOver20000: parseFloat(e.target.value) })
                      }
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowYearForm(false)}
                    className="w-full sm:w-auto px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold active:scale-95"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-semibold active:scale-95"
                  >
                    {saving ? 'Opretter...' : 'Opret år'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Year list */}
            <div>
              <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Dine år</h4>
              <div className="space-y-3">
                {fiscalYears.map((year) => (
                  <div
                    key={year.id}
                    onClick={() => setSelectedYear(year)}
                    className={`p-4 sm:p-5 border-2 rounded-xl cursor-pointer transition-all active:scale-[0.98] ${
                      selectedYear?.id === year.id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-bold text-gray-900 text-lg">{year.year}</h5>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">
                            Under 20k: {year.rateUnder20000} kr | Over 20k: {year.rateOver20000} kr
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteYear(year.id);
                        }}
                        className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 active:scale-95"
                        aria-label="Slet år"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
                {fiscalYears.length === 0 && (
                  <p className="text-gray-500 text-center py-12 text-sm">Ingen år oprettet endnu</p>
                )}
              </div>
            </div>

            {/* Months panel */}
            <div>
              {selectedYear ? (
                <>
                  <div className="mb-4 sm:mb-5">
                    <h4 className="font-bold text-gray-900 mb-3 text-base sm:text-lg">
                      Rediger år {selectedYear.year}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { label: 'Sats under 20.000 km', key: 'rateUnder20000' as const },
                        { label: 'Sats over 20.000 km', key: 'rateOver20000' as const },
                      ].map(({ label, key }) => (
                        <div key={key}>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            {label}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={selectedYear[key]}
                            onChange={(e) =>
                              setSelectedYear({
                                ...selectedYear,
                                [key]: parseFloat(e.target.value),
                              })
                            }
                            onBlur={() =>
                              handleUpdateYear(selectedYear.id, {
                                rateUnder20000: selectedYear.rateUnder20000,
                                rateOver20000: selectedYear.rateOver20000,
                              })
                            }
                            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">
                    Måneder
                  </h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {fiscalMonths.map((month) => (
                      <div
                        key={month.id}
                        className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm"
                      >
                        <div className="flex items-center space-x-2 mb-3">
                          <Edit2 className="w-4 h-4 text-blue-600" />
                          <h5 className="font-semibold text-gray-900">{month.monthName}</h5>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Start dato
                            </label>
                            <input
                              type="date"
                              value={month.startDate}
                              onChange={(e) =>
                                handleUpdateMonth(month.id, {
                                  startDate: e.target.value,
                                  endDate: month.endDate,
                                })
                              }
                              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Slut dato
                            </label>
                            <input
                              type="date"
                              value={month.endDate}
                              onChange={(e) =>
                                handleUpdateMonth(month.id, {
                                  startDate: month.startDate,
                                  endDate: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center py-12 text-sm">
                  Vælg et år for at se måneder
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
